/**
 * Meeting-prep cross-panel bridge (TASK-715).
 *
 * The prep-DOMAIN coordination that survived prepChatStore's deletion: the
 * active brief section highlight, the tab switcher, and the live brief
 * snapshot reader (read at send time so the agent never sees a stale brief).
 * Chat transport/state lives in the dock's meeting-scope store.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface MeetingPrepBridgeState {
  activeBriefSection: string | null;
  setActiveBriefSection: (section: string | null) => void;

  _switchTabFn: ((tab: string) => void) | null;
  setSwitchTab: (fn: ((tab: string) => void) | null) => void;
  switchTab: (tab: string) => void;

  _getBriefSnapshotFn: (() => Record<string, unknown> | null) | null;
  setGetBriefSnapshot: (fn: (() => Record<string, unknown> | null) | null) => void;
  getBriefSnapshotLive: () => Record<string, unknown> | null;

  reset: () => void;
}

export const useMeetingPrepBridge = create<MeetingPrepBridgeState>()(
  devtools(
    (set, get) => ({
      activeBriefSection: null,
      setActiveBriefSection: (section) => set({ activeBriefSection: section }),

      _switchTabFn: null,
      setSwitchTab: (fn) => set({ _switchTabFn: fn }),
      switchTab: (tab) => get()._switchTabFn?.(tab),

      _getBriefSnapshotFn: null,
      setGetBriefSnapshot: (fn) => set({ _getBriefSnapshotFn: fn }),
      getBriefSnapshotLive: () => get()._getBriefSnapshotFn?.() ?? null,

      reset: () =>
        set({ activeBriefSection: null, _switchTabFn: null, _getBriefSnapshotFn: null }),
    }),
    { name: 'MeetingPrepBridge' }
  )
);
